import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import { getLogo } from '../../services/Functions';
import gepcoBillHeaderLogo from '../../images/DISCO Logos/GEPCO-bill-header.png';
import meterReadingSample from '../../images/meter-reading-sample.png';
import './ElectricityConsumerBill.scss';

const LabelRow = ({ en, urdu, urduOnly = false }) => (
    <div className="label-row">
        {!urduOnly && en ? <span className="en-lbl">{en}</span> : null}
        {urdu ? <span className="ur-lbl">{urdu}</span> : null}
    </div>
);

const ValSpace = ({ children, className = '' }) => (
    <div className={`val-space ${className}`.trim()}>{children}</div>
);

const GridColCell = ({ children, divider = false, className = '' }) => (
    <div className={`grid-col-cell ${divider ? 'border-divider-left' : ''} ${className}`.trim()}>{children}</div>
);

const FieldBlock = ({ en, urdu, value, urduOnly = false, valueClassName = '' }) => (
    <>
        <LabelRow en={en} urdu={urdu} urduOnly={urduOnly} />
        <ValSpace className={valueClassName}>{value || ''}</ValSpace>
        <div style={{ height: '1px' }} />
    </>
);

const ChargesBreakdown = ({ billData }) => (
    <div className="charges-breakdown-grid">
        <div className="charges-breakdown-col charges-breakdown-col--left">
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Total Electricity Charges</span>
                    <span className="charges-bd-ur">کل چارجز</span>
                </div>
                <span className="charges-bd-val">{billData.totalElectricityCharges}</span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Subsidies</span>
                    <span className="charges-bd-ur">رعایت</span>
                </div>
                <span className="charges-bd-val">{billData.subsidies}</span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Net Electricity Charges</span>
                    {billData.netElectricityPercent ? (
                        <span className="charges-bd-pct">{billData.netElectricityPercent}</span>
                    ) : null}
                    <span className="charges-bd-ur">نیٹ چارجز</span>
                </div>
                <span className="charges-bd-val">{billData.netElectricityCharges}</span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Taxes</span>
                    {billData.taxesPercent ? (
                        <span className="charges-bd-pct">{billData.taxesPercent}</span>
                    ) : null}
                    <span className="charges-bd-ur">کل ٹیکس</span>
                </div>
                <span className="charges-bd-val">{billData.taxes}</span>
            </div>
            <div className="charges-bd-row charges-bd-row--current">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Current Bill</span>
                    <span className="charges-bd-ur">موجودہ بل</span>
                </div>
                <span className="charges-bd-val">{billData.grandTotal}</span>
            </div>
            <div className="charges-bd-row charges-bd-row--grand">
                <div className="charges-bd-label">
                    <span className="charges-bd-en">Grand Total</span>
                    <span className="charges-bd-ur">کل واجبات</span>
                </div>
                <span className="charges-bd-val">{billData.grandTotal}</span>
            </div>
        </div>
        <div className="charges-breakdown-col charges-breakdown-col--right">
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en charges-bd-en--strong">Arrears</span>
                    <span className="charges-bd-ur">بقایاجات</span>
                </div>
                <span className="charges-bd-val">{billData.arrears}</span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en charges-bd-en--strong">Installment</span>
                    <span className="charges-bd-ur">قسط</span>
                </div>
                <span className="charges-bd-val">{billData.installment}</span>
            </div>
            <div className="charges-bd-row">
                <div className="charges-bd-label">
                    <span className="charges-bd-en charges-bd-en--strong">Adjustments</span>
                    <span className="charges-bd-ur">ایڈجسٹمنٹ</span>
                </div>
                <span className="charges-bd-val charges-bd-val--strong">{billData.adjustments}</span>
            </div>
            {billData.applicationId ? (
                <div className="charges-bd-tracking-block">
                    <div className="charges-bd-tracking-title">ADVANCE BILL</div>
                    <div className="charges-bd-tracking-label">TRACKING ID</div>
                    <div className="charges-bd-tracking-label">{billData.applicationId}</div>
                </div>
            ) : null}
            {billData.totalFpa !== undefined && billData.totalFpa !== null && billData.totalFpa !== '' ? (
                <div className="charges-bd-row">
                    <div className="charges-bd-label">
                        <span className="charges-bd-en charges-bd-en--strong">Total FPA</span>
                    </div>
                    <span className="charges-bd-val charges-bd-val--strong">{billData.totalFpa}</span>
                </div>
            ) : null}
        </div>
    </div>
);

const HistoryBlockGrid = ({ rows = [], className = '', rowCount = 6, footer = null }) => {
    const paddedRows = [...rows];
    while (paddedRows.length < rowCount) {
        paddedRows.push({ month: '', status: '', units: '', bill: '', payment: '' });
    }

    return (
        <div className={`history-block-grid ${className}`.trim()} style={{ '--history-rows': rowCount }}>
            <div className="history-header-row">
                <div className="history-header-cell history-header-cell--month">MONTH</div>
                <div className="history-header-cell history-header-cell--status">STATUS</div>
                <div className="history-header-cell history-header-cell--num">UNITS</div>
                <div className="history-header-cell history-header-cell--num">BILL (RS.)</div>
                <div className="history-header-cell history-header-cell--num">PAYMENT (RS.)</div>
            </div>
            {paddedRows.slice(0, rowCount).map((row, index) => (
                <div className="history-row" key={`${row.month || 'empty'}-${index}`}>
                    <div className="history-cell history-cell--month">{row.month}</div>
                    <div className="history-cell history-cell--status">
                        {row.month ? (
                            <span className={`history-status-pill${row.status ? '' : ' history-status-pill--empty'}`}>
                                {row.status || ''}
                            </span>
                        ) : null}
                    </div>
                    <div className="history-cell history-cell--num">{row.units}</div>
                    <div className="history-cell history-cell--num">{row.bill}</div>
                    <div className="history-cell history-cell--num history-cell--payment">{row.payment}</div>
                </div>
            ))}
            {footer ? <div className="history-table-footer">{footer}</div> : null}
        </div>
    );
};

const BillMessagesBanner = ({ billData, subsidyQrUrl, className = '' }) => {
    if (billData.billMessagesLayoutOnly) {
        return (
            <div className={`bill-messages-banner ${className}`.trim()}>
                <div className="bill-messages-fpa bill-messages-fpa--empty" aria-hidden="true" />
                <div className="bill-messages-subsidy-block bill-messages-subsidy-block--empty">
                    <div className="bill-messages-subsidy-text" aria-hidden="true" />
                </div>
                <div className="bill-messages-subsidy-qr bill-messages-subsidy-qr--empty" aria-hidden="true" />
            </div>
        );
    }

    const totalCharges = billData.totalElectricityCharges || '0';
    const subsidyAmount = String(billData.subsidies || '0').replace('-', '') || '0';

    return (
        <div className={`bill-messages-banner ${className}`.trim()}>
            {billData.fpaMessageUrdu ? (
                <div className="bill-messages-fpa" lang="ur" dir="rtl">
                    {billData.fpaMessageUrdu}
                </div>
            ) : null}
            <div className="bill-messages-subsidy-block">
                <div className="bill-messages-subsidy-text">
                    {billData.noticeUrdu || (
                        <>
                            آپ کے موجودہ بجلی کے بل <strong>{totalCharges}</strong> روپے میں حکومتِ پاکستان کی جانب سے{' '}
                            <strong>{subsidyAmount}</strong> روپے کی سبسڈی شامل ہے۔ اس سبسڈی کی مجوزہ اہلیت کی تصدیق کے لیے
                            QR کوڈ اسکین کریں اور رجسٹریشن کروائیں۔ بصورتِ دیگر آپ کی سبسڈی معطل ہو سکتی ہے۔
                        </>
                    )}
                </div>
            </div>
            {subsidyQrUrl ? (
                <div className="bill-messages-subsidy-qr">
                    <img src={subsidyQrUrl} alt="Subsidy QR" />
                </div>
            ) : null}
        </div>
    );
};

const MeterSnapPanel = ({ billData }) => {
    const metaLine1 =
        billData.meterSnapMetaLine1 ||
        [billData.meterSnapCapturedBy, billData.meterSnapCapturedAt, billData.meterSnapReference]
            .filter(Boolean)
            .join(' ');

    const versionLabel = billData.meterSnapVersion
        ? `Ver ${String(billData.meterSnapVersion).replace(/^v/i, '')}`
        : '';

    const metaLine2 =
        billData.meterSnapMetaLine2 ||
        [billData.meterSnapGps, versionLabel].filter(Boolean).join(' ');

    const amountPaidNote = billData.amountPaidNote || '';

    return (
        <div className="meter-snaps-card meter-snaps-card--verify">
            <div className="meter-snap-photo-stage">
                {metaLine1 || metaLine2 ? (
                    <div className="meter-snap-meta meter-snap-meta--overlay">
                        {metaLine1 ? <div className="meter-snap-meta__line">{metaLine1}</div> : null}
                        {metaLine2 ? <div className="meter-snap-meta__line">{metaLine2}</div> : null}
                    </div>
                ) : null}
                {billData.meterImageUrl ? (
                    <div className="meter-snap-photo-inner">
                        <img
                            src={billData.meterImageUrl}
                            alt="Meter reading"
                            className="meter-snap-photo"
                            style={{
                                objectFit: billData.meterImageFit || 'contain',
                                objectPosition: billData.meterImagePosition || 'top center',
                            }}
                        />
                    </div>
                ) : (
                    <div className="meter-snap-placeholder meter-snap-placeholder--empty" aria-hidden="true" />
                )}
                {billData.paidOverlay ? (
                    <div
                        className="bill-payment-logo"
                        style={{ backgroundImage: `url('${billData.paidOverlay}')` }}
                    />
                ) : billData.showPaidStamp ? (
                    <div className="paid-stamp-badge" aria-hidden="true">
                        <span className="paid-stamp-badge__pre">BILL PAID</span>
                        <span className="paid-stamp-badge__main">PAID</span>
                        <span className="paid-stamp-badge__stars">★★★</span>
                        <span className="paid-stamp-badge__sub">VERIFIED BY PITC</span>
                    </div>
                ) : null}
            </div>
            {amountPaidNote ? <div className="meter-snap-paid-footer">{amountPaidNote}</div> : null}
        </div>
    );
};

const SlipMatrixGrid = ({ billData }) => (
    <div className="slip-matrix-grid">
        <div className="slip-matrix-stack slip-matrix-stack--bill-month">
            <div className="slip-matrix-label">
                BILL MONTH <span className="ur-lbl">مہینہ بل</span>
            </div>
            <div className="slip-matrix-stack-value">{billData.billMonth}</div>
        </div>
        <div className="slip-matrix-stack slip-matrix-stack--due-date">
            <div className="slip-matrix-label">
                DUE DATE <span className="ur-lbl">مقررہ تاریخ</span>
            </div>
            <div className="slip-matrix-stack-value">{billData.dueDate}</div>
        </div>
        <div className="slip-matrix-label slip-matrix-label--payable-within">
            PAYABLE WITHIN DUE DATE <span className="ur-lbl">مقررہ تاریخ تک واجب الادا</span>
        </div>
        <div className="slip-matrix-amount slip-matrix-amount--main">{billData.payableWithinDueDate}</div>
        <div className="slip-matrix-label slip-matrix-label--payable-after">
            PAYABLE AFTER DUE DATE <span className="ur-lbl">مقررہ تاریخ کے بعد واجب الادا</span>
        </div>
        <div className="slip-matrix-late slip-matrix-late--till">
            <span>Till {billData.surchargeTillDate || billData.dueDate}</span>
            <strong>{billData.payableAfterDueDateTill}</strong>
        </div>
        <div className="slip-matrix-late slip-matrix-late--after">
            <span>After {billData.surchargeAfterDate || billData.dueDate}</span>
            <strong>{billData.payableAfterDueDateAfter}</strong>
        </div>
    </div>
);

const ElectricityConsumerBill = ({ billData = {}, className = '', id }) => {
    const safeBillData = billData || {};
    const [headerQrUrl, setHeaderQrUrl] = useState('');
    const [sidebarQrUrl, setSidebarQrUrl] = useState('');
    const [subsidyQrUrl, setSubsidyQrUrl] = useState('');
    const barcodeRef = useRef(null);

    const qrValue = String(
        safeBillData.raastQr ||
            safeBillData.barcodeRaw ||
            safeBillData.referenceNo ||
            safeBillData.barcodeValue ||
            safeBillData.consumerId ||
            ''
    ).trim();

    const subsidyQrValue =
        safeBillData.raastQr ||
        safeBillData.subsidyQrUrl ||
        safeBillData.subsidyEligibilityUrl ||
        '';

    const hideQrContent = Boolean(safeBillData.hideQrContent);
    const hideHeaderUrduText = Boolean(safeBillData.hideHeaderUrduText);

    useEffect(() => {
        if (hideQrContent) {
            setHeaderQrUrl('');
            setSidebarQrUrl('');
            setSubsidyQrUrl('');
            return undefined;
        }

        const makeQr = (value, setter, width = 120) => {
            if (!value || value === '-') {
                setter('');
                return Promise.resolve();
            }
            return QRCode.toDataURL(String(value), {
                errorCorrectionLevel: 'M',
                margin: 0,
                width,
            })
                .then(setter)
                .catch(() => setter(''));
        };

        makeQr(qrValue, setHeaderQrUrl, 180);
        makeQr(qrValue, setSidebarQrUrl, 90);
        makeQr(subsidyQrValue || qrValue, setSubsidyQrUrl, 70);
        return undefined;
    }, [qrValue, subsidyQrValue, hideQrContent]);

    useEffect(() => {
        if (!barcodeRef.current) return;
        const barcodeValue = safeBillData.barcodeRaw || safeBillData.barcodeValue;
        if (!barcodeValue) {
            barcodeRef.current.innerHTML = '';
            return;
        }
        try {
            JsBarcode(barcodeRef.current, String(barcodeValue).replace(/\s/g, ''), {
                width: 1.1,
                height: 38,
                margin: 2,
                displayValue: false,
            });
        } catch {
            barcodeRef.current.innerHTML = '';
        }
    }, [safeBillData.barcodeRaw, safeBillData.barcodeValue]);

    if (!billData) return null;

    const logo = safeBillData.discoCode === '12' ? gepcoBillHeaderLogo : getLogo(safeBillData.discoCode);
    const historyRows = safeBillData.billHistory || [];
    const leftHistory = historyRows.slice(0, Math.ceil(historyRows.length / 2));
    const rightHistory = historyRows.slice(Math.ceil(historyRows.length / 2));
    const addressLines = String(safeBillData.address || '')
        .split(/\n|,\s*/)
        .filter(Boolean);
    const nameLine = safeBillData.consumerName || '';
    const showProRata = /pro-rata/i.test(String(safeBillData.meterNote || safeBillData.status || ''));

    const billBrandStyle = {
        '--bill-logo-color': safeBillData.logoColor || '#1b6b38',
        '--bill-logo-color-light': safeBillData.logoColorLight || '#c8e6d0',
    };

    return (
        <div
            className={`electricity-consumer-bill gbn-print-root ${className}`}
            id={id}
            style={billBrandStyle}
        >
            <div className="a4-container maincontent">
                <header className="bill-header">
                    <div className="header-left">
                        <img className="header-brand-logo" src={logo} alt={safeBillData.companyShortName || 'DISCO'} />
                        <div className="ellipsis-protect header-gst">
                            {safeBillData.gstLabel ||
                                `${safeBillData.companyShortName ? `${safeBillData.companyShortName} ` : ''}GST NO: ${safeBillData.gstNo}`}
                        </div>
                        <div className="ellipsis-protect header-website">{safeBillData.website}</div>
                        <div className="text-danger ellipsis-protect header-corruption">Say No To Corruption</div>
                    </div>
                    <div className="header-center">
                        <h1 className="brand-logo ellipsis-protect">{safeBillData.companyTitle}</h1>
                        <h2 className="ellipsis-protect header-tagline">YOUR BETTER SERVICE – OUR PRIDE</h2>
                        <h3 className="brand-blue ellipsis-protect">ELECTRICITY CONSUMER BILL</h3>
                    </div>
                    <div className="header-right">
                        {hideQrContent ? (
                            <div className="header-qr-urdu-row header-qr-urdu-row--empty" aria-hidden="true" />
                        ) : (
                            <div className="header-qr-urdu-row">
                                {headerQrUrl ? (
                                    <span className="header-qr-host">
                                        <img src={headerQrUrl} alt="Bill QR Code" />
                                    </span>
                                ) : null}
                                <div className="header-urdu-text">
                                    <div className="ur-lbl header-payment-urdu">
                                        {hideHeaderUrduText
                                            ? ''
                                            : safeBillData.headerQrUrdu ||
                                              'راست اپنائیں۔ QR کوڈ سے ادائیگی کریں۔ انعام پائیں۔'}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </header>

                <main className="main-dashboard-grid">
                    <div className="bill-section bill-section--consumer component-card">
                        <div className="card-header">☰ CONSUMER DETAIL</div>
                        <div className="grid-4col">
                            <GridColCell>
                                <FieldBlock en="REFERENCE NO" urdu="حوالہ نمبر" value={safeBillData.referenceNo} />
                                <FieldBlock en="CONSUMER ID" urdu="کنزیومر نمبر" value={safeBillData.consumerId} />
                                <LabelRow urdu="نام و پتہ" urduOnly />
                                <ValSpace className="val-space--address">
                                    {nameLine ? <span>{nameLine}</span> : null}
                                    {addressLines.map((line) => (
                                        <span key={line}>{line}</span>
                                    ))}
                                </ValSpace>
                            </GridColCell>
                            <GridColCell divider>
                                <FieldBlock en="TRANSFORMER" urdu="ٹرانسفارمر" value={safeBillData.transformer} />
                                <FieldBlock en="FEEDER" urdu="فیڈر" value={safeBillData.feeder} />
                                <FieldBlock en="SUB DIVISION" urdu="سب ڈویژن" value={safeBillData.subDivision} />
                            </GridColCell>
                            <GridColCell divider>
                                <FieldBlock en="CATEGORY" urdu="قسم" value={safeBillData.category} />
                                <FieldBlock en="STATUS" urdu="حالت میٹر" value={safeBillData.status} />
                                <FieldBlock en="TARIFF CATEGORY" urdu="ٹیرف قسم" value={safeBillData.tariffCategory} />
                            </GridColCell>
                            <GridColCell divider>
                                <FieldBlock en="SAN LOAD" urdu="لوڈ" value={safeBillData.sanctionedLoad} />
                                <FieldBlock en="CURR MDI" urdu="ایم ڈی آئی" value={safeBillData.mdi} />
                                <FieldBlock en="TARIFF" urdu="ٹیرف" value={safeBillData.tariff} />
                            </GridColCell>
                        </div>
                    </div>

                    <div className="bill-section bill-section--meter component-card">
                        <div className="card-header">⏳ METER INFO</div>
                        <div className="grid-4col">
                            <GridColCell className="meter-reading-col">
                                <FieldBlock en="MF" urdu="جزو ضرب" value={safeBillData.meterFactor} valueClassName="val-space--meter" />
                            </GridColCell>
                            <GridColCell divider className="meter-reading-col">
                                <FieldBlock en="PREVIOUS READING" urdu="سابقہ ریڈنگ" value={safeBillData.previousReading} valueClassName="val-space--meter" />
                            </GridColCell>
                            <GridColCell divider className="meter-reading-col">
                                <FieldBlock en="PRESENT READING" urdu="موجودہ ریڈنگ" value={safeBillData.presentReading} valueClassName="val-space--meter" />
                            </GridColCell>
                            <GridColCell divider className="meter-reading-col">
                                <FieldBlock en="UNITS" urdu="یونٹ" value={safeBillData.units} valueClassName="val-space--meter" />
                            </GridColCell>
                        </div>
                        <div className="meter-info-footer">
                            <span>
                                <strong>METER NO:</strong> {safeBillData.meterNo}
                            </span>
                            {safeBillData.meterNote && safeBillData.meterNote !== '-' ? (
                                <span className="pro-rata-message">{safeBillData.meterNote}</span>
                            ) : null}
                        </div>
                    </div>

                    <div className="bill-section bill-section--charges component-card charges-breakdown-card">
                        <div className="card-header">
                            BILL CHARGES BREAKDOWN <span className="charges-breakdown-rs">(RS.)</span>
                        </div>
                        <ChargesBreakdown billData={safeBillData} />
                    </div>

                    <div className="bill-section bill-section--sidebar">
                        <div className="right-panel-box">
                            <div className="right-section-cell">
                                <div className="right-section-label">
                                    <span>BILL MONTH</span>
                                    <span className="ur-lbl">مہینہ بل</span>
                                </div>
                                <div className="right-main-val right-main-val--month">{safeBillData.billMonth}</div>
                            </div>
                            <div className="right-grid-2col">
                                <div className="right-grid-cell">
                                    <span className="right-grid-en">
                                        READING DATE
                                        {showProRata ? <span className="pro-rata-tag"> (Pro-Rata)</span> : null}
                                    </span>
                                    <span className="ur-lbl">تاریخ ریڈنگ</span>
                                    <span className="right-grid-val">{safeBillData.readingDate}</span>
                                </div>
                                <div className="right-grid-cell">
                                    <span className="right-grid-en">ISSUE DATE</span>
                                    <span className="ur-lbl">تاریخ اجراء</span>
                                    <span className="right-grid-val">{safeBillData.issueDate}</span>
                                </div>
                            </div>
                            <div className="right-section-cell">
                                <div className="right-section-label">
                                    <span className="due-date-label">⚠ DUE DATE</span>
                                    <span className="ur-lbl">مقررہ تاریخ</span>
                                </div>
                                <div className="right-main-val right-main-val--due">{safeBillData.dueDate}</div>
                            </div>
                        </div>

                        <div className="payable-card">
                            <div className="payable-card__labels">
                                <span className="payable-card__en">PAYABLE WITHIN DUE DATE</span>
                                <span className="ur-lbl">مقررہ تاریخ تک واجب الادا</span>
                            </div>
                            <div className="payable-card__amount">{safeBillData.payableWithinDueDate}</div>
                        </div>

                        <div className="surcharge-panel">
                            <div className="surcharge-panel__grid">
                                <div className="surcharge-panel__label-col">
                                    <div>
                                        <div className="surcharge-panel__title">L.P</div>
                                        <div className="surcharge-panel__title">SURCHARGE</div>
                                    </div>
                                    <div className="surcharge-panel__divider">
                                        <div className="surcharge-panel__title">PAYABLE</div>
                                        <div className="surcharge-panel__title">AFTER DUE</div>
                                        <div className="surcharge-panel__title">DATE</div>
                                        <div className="ur-lbl surcharge-panel__urdu">کے بعد واجب الادا</div>
                                    </div>
                                </div>
                                <div className="surcharge-panel__amount-col">
                                    <div className="surcharge-panel__lp">{safeBillData.lpSurcharge}</div>
                                    <div className="surcharge-panel__divider">
                                        <div className="surcharge-panel__date">
                                            Till {safeBillData.surchargeTillDate || safeBillData.dueDate}
                                        </div>
                                        <div className="surcharge-panel__payable">{safeBillData.payableAfterDueDateTill}</div>
                                    </div>
                                </div>
                                <div className="surcharge-panel__amount-col">
                                    <div className="surcharge-panel__lp">
                                        {safeBillData.lpSurchargeAfter || safeBillData.lpSurcharge}
                                    </div>
                                    <div className="surcharge-panel__divider">
                                        <div className="surcharge-panel__date">
                                            After {safeBillData.surchargeAfterDate || safeBillData.dueDate}
                                        </div>
                                        <div className="surcharge-panel__payable">{safeBillData.payableAfterDueDateTill}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <MeterSnapPanel billData={safeBillData} />
                    </div>

                    <BillMessagesBanner
                        billData={safeBillData}
                        subsidyQrUrl={subsidyQrUrl}
                        className="bill-section bill-section--messages"
                    />

                    <div className="bill-section bill-section--history history-card">
                        <div className="history-card-header">
                            <span>BILL HISTORY</span>
                            <span>
                                {safeBillData.companyShortName || 'DISCO'}
                                <br />
                                Consumer
                            </span>
                        </div>
                        <div className="history-data-wrapper">
                            <HistoryBlockGrid
                                rows={leftHistory}
                                footer={
                                    <>
                                        <span className="history-footer-en">Deferred Amount</span>
                                        <span className="history-footer-ur ur-lbl">مؤخر شدہ رقم</span>
                                    </>
                                }
                            />
                            <HistoryBlockGrid
                                rows={rightHistory}
                                className="history-center-divider"
                                footer={
                                    <>
                                        <span className="history-footer-en">Outstanding Install.</span>
                                        <span className="history-footer-ur ur-lbl">بقایا اقساط</span>
                                    </>
                                }
                            />
                        </div>
                    </div>
                </main>

                <footer className="perforated-slip">
                    <div className="slip-branding-grid">
                        <div className="slip-brand-col">
                            <img className="disco-logo-slip" src={logo} alt="" />
                            <div className="bank-stamp-container">
                                <div className="bank-stamp-circle">BANK STAMP</div>
                            </div>
                        </div>
                        <div className="slip-title-area">
                            <h4 className="brand-logo">{safeBillData.companyTitle}</h4>
                            <h4 className="brand-blue">ELECTRICITY CONSUMER BILL</h4>
                            <div className="slip-subtitle">YOUR BETTER SERVICE - OUR PRIDE</div>
                            <div className="slip-urdu-line">
                                وزیر اعظم انرجی ایفیشینٹ فین ریپلیسمنٹ پروگرام کا آغاز
                            </div>
                            <div className="slip-url">https://efan.gov.pk/</div>
                        </div>
                        <div className="slip-barcode-col">
                            <div className="barcode-placeholder" aria-label="Payment barcode">
                                <svg ref={barcodeRef} />
                            </div>
                            {safeBillData.barcodeCaption ? (
                                <div className="barcode-caption">{safeBillData.barcodeCaption}</div>
                            ) : null}
                            <div className="slip-id-box">
                                <div className="slip-id-row">
                                    <span className="slip-id-en">CONSUMER ID</span>
                                    <span className="slip-id-ur ur-lbl">کنزیومر نمبر</span>
                                    <span className="slip-id-value">{safeBillData.consumerId}</span>
                                </div>
                                <div className="slip-id-row">
                                    <span className="slip-id-en">REFERENCE NO</span>
                                    <span className="slip-id-ur ur-lbl">حوالہ نمبر</span>
                                    <span className="slip-id-value">{safeBillData.referenceNo}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <SlipMatrixGrid billData={safeBillData} />
                </footer>
            </div>
        </div>
    );
};

export default ElectricityConsumerBill;
